import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Grid, IconButton, Tooltip } from '@mui/material';

import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelIcon from '@mui/icons-material/Cancel';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import VerificationIcon from '../../../../assets/main/verification-icon.svg';
import { useParams } from 'react-router-dom';
import { useSidebar } from '../../../../context/SidebarContext';
import { useBreadcrumb } from '../../../../context/BreadcrumbContext';
import TotalOrgIcon from '../../../../assets/organization/total-organization-icon.svg';
import apiCall from '../../../../utils/api';
import { API_URL } from '../../../../utils/env';
import { CircularProgress, Checkbox, FormControlLabel } from '@mui/material';
import FacilityList from './components/FacilityList';
import WorkerList from './components/WorkerList';
import AlertPopup from '../../../../components/common/AlertPopup';
import ConfirmationDialog from '../../../../components/common/ConfirmationDialog';

import {
  PageWrapper,
  HeaderCard,
  IconWrapper,
  HeaderInfo,
  StatusBadge,
  SuspendedTag,
  ContentPaper,
  StyledTabs,
  StyledTab,
  TabPanelContainer,
  SectionHeader,
  SectionTitle,
  DetailRow,
  Label,
  Value,
  DocsContainer,
  DocIconBox,
  DateTextField,
  DocumentExpiring,
  ButtonGroupContainer,
  VerificationImageContainer,
  ActionCard,
} from './styles';
import ActionPopup from '../../../../components/common/ActionPopup';
import DocumentPreviewPopup from '../../../../components/common/DocumentPreviewPopup';
import { calculateDaysLeft, truncateLast, getFileNameFromPath } from '../../../../helpers';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../../store';
import { updateOrganizationSignupDocumentStatus } from '../../../../modules/superadmin/actions';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`org-tabpanel-${index}`}
      aria-labelledby={`org-tab-${index}`}
      {...other}
    >
      {value === index && (
        <TabPanelContainer>
          {children}
        </TabPanelContainer>
      )}
    </div>
  );
}

const OrganizationProfileReviewPage: React.FC = () => {
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  const [tabValue, setTabValue] = useState(0);

  const [org, setOrg] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Document review states
  const [showRejectPopup, setShowRejectPopup] = useState(false);
  const [showAcceptPopup, setShowAcceptPopup] = useState(false);
  const [showPreviewPopup, setShowPreviewPopup] = useState(false);
  const [documentToReject, setDocumentToReject] = useState<any>(null);
  const [documentToAccept, setDocumentToAccept] = useState<any>(null);
  const [previewFileUrl, setPreviewFileUrl] = useState('');
  const [previewFileType, setPreviewFileType] = useState<'image' | 'pdf' | 'other'>('other');
  const [previewFileName, setPreviewFileName] = useState('');
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);
  const [confirmation, setConfirmation] = useState<{ open: boolean; title: string; message: string; onConfirm: (() => void) | null }>({ open: false, title: '', message: '', onConfirm: null });
  const [processing, setProcessing] = useState(false);

  const fetchOrgDetails = useCallback(async () => {
    try {
      const response = await apiCall<any>('get', `/api/organization/superadmin/detail/${id}/`);
      if (response.data && response.data.data) {
        setOrg(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching org details:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const handleDocumentDateChange = async (docId: number, field: 'issue_date' | 'expiry_date', value: dayjs.Dayjs | null) => {
    const dateStr = value && value.isValid() ? value.format('YYYY-MM-DD') : null;
    
    // 1. Optimistically update local state so UI is immediately responsive
    setOrg((prev: any) => ({
      ...prev,
      signup_data: prev.signup_data.map((d: any) => 
        d.id === docId ? { ...d, [field]: dateStr } : d
      )
    }));

    // 2. Call the API in the background to save the date if it's either null (cleared) or a valid date
    if (value === null || (value && value.isValid())) {
      try {
        await dispatch(updateOrganizationSignupDocumentStatus({
          document_id: docId,
          [field]: dateStr
        }) as any);
      } catch (error) {
        console.error('Failed to auto-save document date:', error);
      }
    }
  };

  const getFileType = (url: string): 'image' | 'pdf' | 'other' => {
    const ext = url.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext || '')) return 'image';
    if (ext === 'pdf') return 'pdf';
    return 'other';
  };

  const getRelativePath = (url: string) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      return urlObj.pathname.substring(1);
    } catch (e) {
      return url;
    }
  };

  const handleDownload = async (url: string, fileName: string) => {
    try {
      let blob: Blob;
      if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Network response was not ok');
        blob = await response.blob();
      } else {
        const response = await apiCall<Blob>(
          'get',
          `/api/fetch-s3-file/?file_path=${encodeURIComponent(url)}&file_format=blob`,
          undefined,
          undefined,
          'blob'
        );
        blob = response.data;
      }

      if (!(blob instanceof Blob)) {
        throw new Error('Failed to get a valid file');
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      const fallbackUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
    }
  };

  useEffect(() => {
    fetchOrgDetails();
  }, [fetchOrgDetails]);

  useEffect(() => {
    setActivePage('organizations');
    setPath([
      { title: 'Super Admin', path: '/super-admin' },
      { title: 'Organizations', path: '/super-admin/organizations' },
      { title: 'Overview', path: `/super-admin/organizations/${id}` },
      { title: 'Profile Review', path: `/super-admin/organizations/${id}/profile` },
    ]);
    setIsShow(true);
  }, [setActivePage, setPath, setIsShow, id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleToggleVerification = async (verified: boolean) => {
    setProcessing(true);
    try {
      const response = await apiCall('patch', `/api/organization/verify/${id}/`, { is_verified: verified });
      if (response.status === 200) {
        setOrg((prev: any) => ({ ...prev, is_verified: verified }));
        setAlert({ message: `Organization ${verified ? 'verified' : 'unverified'} successfully!`, severity: 'success' });
      }
    } catch (error: any) {
      setAlert({ message: error.response?.data?.message || 'Failed to update verification status', severity: 'error' });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!org) {
    return (
      <PageWrapper>
        <Typography>Organization not found</Typography>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <HeaderCard elevation={0}>
        <IconWrapper>
          <img src={TotalOrgIcon} alt="Org Icon" style={{ width: '32px', height: '32px', filter: 'grayscale(100%)' }} />
        </IconWrapper>
        <HeaderInfo>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
            {org.org_name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
            <Typography variant="body2" sx={{ color: '#666', mr: 1 }}>
              ID: {org.org_id}
            </Typography>
            <StatusBadge status={org.status}>
              {org.status}
            </StatusBadge>
          </Box>
        </HeaderInfo>
        {org.status === 'suspended' && (
          <SuspendedTag>
            suspended
          </SuspendedTag>
        )}
      </HeaderCard>

      <ContentPaper elevation={0}>
        <StyledTabs value={tabValue} onChange={handleTabChange} aria-label="organization details tabs">
          <StyledTab label="Organization Details" />
          <StyledTab label="Facilities" />
          <StyledTab label="Workers" />
        </StyledTabs>

        <TabPanel value={tabValue} index={0}>
          <SectionHeader>
            <SectionTitle>Basic Details</SectionTitle>
          </SectionHeader>
          
          <DetailRow container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Label>Organization Name</Label>
              <Value>{org.org_name}</Value>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Label>Email Address</Label>
              <Value>{org.owner_details?.email || org.email || 'N/A'}</Value>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Label>Phone Number</Label>
              <Value>{org.contact_number}</Value>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Label>Address</Label>
              <Value>{org.address || 'N/A'}</Value>
            </Grid>
            {/* Dynamic Text Fields */}
            {org.signup_data?.filter((d: any) => !d.file_value).map((data: any) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={data.id}>
                <Label>{data.field_label}</Label>
                <Value>{data.value || 'N/A'}</Value>
              </Grid>
            ))}
          </DetailRow>

          <SectionHeader>
            <SectionTitle>Documents Uploaded</SectionTitle>
          </SectionHeader>
          
          <Box sx={{ mt: 2 }}>
            {org.signup_data?.filter((d: any) => d.file_value).length > 0 ? (
              org.signup_data?.filter((d: any) => d.file_value).map((doc: any) => {
                const fileName = getFileNameFromPath(doc.file_value);
                const daysLeft = doc.expiry_date ? calculateDaysLeft(doc.expiry_date) : null;
                const isExpiringSoon = daysLeft !== null && daysLeft <= 15 && daysLeft >= 0;

                return (
                  <DocsContainer key={doc.id} sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <DocIconBox>
                          <DescriptionIcon />
                        </DocIconBox>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111827', mb: 0.2 }}>
                            {doc.field_label}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography 
                              variant="caption" 
                              sx={{ color: '#6B7280', fontFamily: 'monospace', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                              onClick={() => {
                                const relPath = getRelativePath(doc.file_value);
                                setPreviewFileUrl(relPath);
                                setPreviewFileType(getFileType(relPath));
                                setPreviewFileName(doc.field_label);
                                setShowPreviewPopup(true);
                              }}
                            >
                              {truncateLast(fileName)}
                            </Typography>
                            {doc.is_accepted && <CheckCircleRoundedIcon color='primary' sx={{ fontSize: 16 }} />}
                            {doc.is_rejected && <CancelIcon color='error' sx={{ fontSize: 16 }} />}
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                        {/* Issued Date */}
                        <Box>
                          <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
                            Issued:
                          </Typography>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker 
                              format="DD/MM/YYYY"
                              enableAccessibleFieldDOMStructure={false}
                              value={doc.issue_date ? dayjs(doc.issue_date) : null}
                              onChange={(newValue) => handleDocumentDateChange(doc.id, 'issue_date', newValue)}
                              slots={{ textField: DateTextField }}
                              slotProps={{
                                textField: { variant: 'outlined', size: 'small' }
                              }}
                            />
                          </LocalizationProvider>
                        </Box>

                        <Typography variant="body2" sx={{ pb: 1 }}>to</Typography>

                        {/* Expiry Date */}
                        <Box>
                          <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
                            Expires:
                          </Typography>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker 
                              format="DD/MM/YYYY"
                              enableAccessibleFieldDOMStructure={false}
                              value={doc.expiry_date ? dayjs(doc.expiry_date) : null}
                              onChange={(newValue) => handleDocumentDateChange(doc.id, 'expiry_date', newValue)}
                              slots={{ textField: DateTextField }}
                              slotProps={{
                                textField: { variant: 'outlined', size: 'small' }
                              }}
                            />
                          </LocalizationProvider>
                        </Box>

                        {/* Actions */}
                        <Box sx={{ display: 'flex', gap: 0.5, pb: 0.2 }}>
                          <Tooltip title="Preview">
                            <IconButton 
                              size="small"
                              onClick={() => {
                                const relPath = getRelativePath(doc.file_value);
                                setPreviewFileUrl(relPath);
                                setPreviewFileType(getFileType(relPath));
                                setPreviewFileName(doc.field_label);
                                setShowPreviewPopup(true);
                              }}
                            >
                              <RemoveRedEyeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton 
                              size="small"
                              onClick={() => {
                                const relPath = getRelativePath(doc.file_value);
                                handleDownload(relPath, fileName);
                              }}
                              sx={{ color: 'primary.main' }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Approve">
                            <span>
                              <IconButton 
                                size="small"
                                color="success"
                                onClick={() => {
                                  setDocumentToAccept(doc);
                                  setShowAcceptPopup(true);
                                }}
                                disabled={statusUpdateLoading || doc.is_accepted}
                              >
                                <CheckCircleRoundedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <span>
                              <IconButton 
                                size="small"
                                color="error"
                                onClick={() => {
                                  setDocumentToReject(doc);
                                  setShowRejectPopup(true);
                                }}
                                disabled={statusUpdateLoading || doc.is_rejected}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>

                    {isExpiringSoon && (
                      <DocumentExpiring>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          EXPIRES in {daysLeft} days! Urgent action required
                        </Typography>
                      </DocumentExpiring>
                    )}
                    {doc.remarks && (
                      <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', borderLeft: '2px solid #e0e0e0', pl: 1 }}>
                        Note: {doc.remarks}
                      </Typography>
                    )}
                  </DocsContainer>
                );
              })
            ) : (
              <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px dashed #ccc' }}>
                <Typography variant="body2" color="text.secondary">No documents uploaded</Typography>
              </Box>
            )}
          </Box>
          
          <ActionCard elevation={0}>
            <Grid container>
              <Grid size={6}>
                <Grid container spacing={2}>
                  <VerificationImageContainer size={1}>
                    <img src={VerificationIcon} alt="Verification Icon" />
                  </VerificationImageContainer>
                  <Grid size={11}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#333' }}>
                      Verification Action
                    </Typography>
                    <Typography variant='caption' sx={{ color: '#999' }}>
                      Confirm the organization's eligibility and finalize their status
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid size={6}>
                <ButtonGroupContainer>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={org.is_verified}
                        onChange={(event) => {
                          const newStatus = event.target.checked;
                          setConfirmation({
                            open: true,
                            title: `Confirm ${newStatus ? 'Verification' : 'Un-verification'}`,
                            message: `Are you sure you want to ${newStatus ? 'verify' : 'un-verify'} this organization?`,
                            onConfirm: () => {
                              handleToggleVerification(newStatus);
                              setConfirmation({ open: false, title: '', message: '', onConfirm: null });
                            },
                          });
                        }}
                        disabled={processing || org.status === 'approved'}
                        sx={{
                          color: '#5E8E7E',
                          '&.Mui-checked': {
                            color: '#5E8E7E',
                          },
                        }}
                      />
                    }
                    label={<Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>Verified</Typography>}
                  />
                  {processing && <CircularProgress size={20} sx={{ ml: 1 }} />}
                </ButtonGroupContainer>
              </Grid>
            </Grid>
          </ActionCard>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <FacilityList orgId={id!} />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <WorkerList orgId={id!} />
        </TabPanel>
      </ContentPaper>

      <AlertPopup 
        message={alert?.message || ''} 
        severity={alert?.severity || 'success'} 
        open={!!alert} 
        onClose={() => setAlert(null)} 
      />
      
      <ConfirmationDialog
        open={confirmation.open}
        onClose={() => setConfirmation({ open: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmation.onConfirm || (() => { })}
        title={confirmation.title}
        message={confirmation.message}
      />

      <ActionPopup
        open={showRejectPopup}
        onClose={() => {
          setShowRejectPopup(false);
          setDocumentToReject(null);
        }}
        onSubmit={async (remark) => {
          if (!documentToReject) return;
          setStatusUpdateLoading(true);
          try {
            const result = await dispatch(updateOrganizationSignupDocumentStatus({
              document_id: documentToReject.id,
              action: 'reject',
              remarks: remark
            }) as any);
            if (updateOrganizationSignupDocumentStatus.fulfilled.match(result)) {
              fetchOrgDetails();
            }
          } catch (error) {
            console.error('Error rejecting document:', error);
          } finally {
            setStatusUpdateLoading(false);
            setShowRejectPopup(false);
            setDocumentToReject(null);
          }
        }}
        title="Reject Document"
        message={`Are you sure you want to reject the document "${documentToReject?.field_label}"? Please provide a remark.`}
        submitButtonText="Reject"
        textFieldLabel="Rejection Remark"
        textFieldPlaceholder="Enter reason for rejection"
        loading={statusUpdateLoading}
        required={true}
      />

      <ActionPopup
        open={showAcceptPopup}
        onClose={() => {
          setShowAcceptPopup(false);
          setDocumentToAccept(null);
        }}
        onSubmit={async (remark) => {
          if (!documentToAccept) return;
          setStatusUpdateLoading(true);
          try {
            const result = await dispatch(updateOrganizationSignupDocumentStatus({
              document_id: documentToAccept.id,
              action: 'accept',
              remarks: remark,
              issue_date: documentToAccept.issue_date,
              expiry_date: documentToAccept.expiry_date
            }) as any);
            if (updateOrganizationSignupDocumentStatus.fulfilled.match(result)) {
              fetchOrgDetails();
            }
          } catch (error) {
            console.error('Error accepting document:', error);
          } finally {
            setStatusUpdateLoading(false);
            setShowAcceptPopup(false);
            setDocumentToAccept(null);
          }
        }}
        title="Accept Document"
        message={`Are you sure you want to accept the document "${documentToAccept?.field_label}"?`}
        submitButtonText="Accept"
        loading={statusUpdateLoading}
        required={false}
      />

      <DocumentPreviewPopup
        open={showPreviewPopup}
        onClose={() => setShowPreviewPopup(false)}
        fileUrl={previewFileUrl}
        fileType={previewFileType}
        fileName={previewFileName}
      />
    </PageWrapper>
  );
};

export default OrganizationProfileReviewPage;
